import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { FormViewService } from '../form-view.service';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { AppService } from '../../../../app.service';
import { environment } from '../../../../../environments/environment';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import {
  firstGridRowObjectFromPayload,
  rowPayloadFromActiveScreen,
} from '../../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../../shared/_utils/safe-json-input';
export type ViewBulkExportSelectionMode = 'FORM' | 'ENTITY';

/**
 * Selection table columns — `field` / `header` mirror legacy
 * `formViewCtrl.js` `ViewBulkExportSelectionCtrl` / `ViewBulkExportEntitySelectionCtrl`
 * (`data` + `title` on gt-datatable2 `columns`, L1494–1508 and L1552–1568).
 * `altFields` covers common API / Oracle casing variants for the same value.
 */
export interface ViewBulkExportColumn {
  field: string;
  header: string;
  widthPx?: number;
  altFields?: string[];
}

@Component({
    selector: 'gtw-view-bulk-export-selection',
    templateUrl: './view-bulk-export-selection.component.html',
    styleUrls: ['./view-bulk-export-selection.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ViewBulkExportSelectionComponent implements OnDestroy, AfterViewInit {
  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private loadStarted = false;
  private initTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingRowData: any = null;
  private pendingHeader: any = null;
  private pendingColData: any = null;
  /** Full grid callback `{ rowData, colData, … }` from CeHostDialog — row id may live here. */
  private pendingGridData: any = null;
  private pendingBaseUrls: any = null;
  private pendingModalName = '';

  rowData: any = {};
  headerData: any = {};
  modalName = '';

  /** Until user theme loads, use light shell so toolbar/search are not white-on-white. */
  currentTheme: string = 'theme-white';
  requestedType: 'Forms' | 'Entities' = 'Forms';
  selectionMode: ViewBulkExportSelectionMode = 'FORM';

  columns: ViewBulkExportColumn[] = [];
  rows: any[] = [];
  filteredRows: any[] = [];
  searchTerm = '';

  /** Client-side sort (legacy `gt-datatable2` had `ordering: true` on similar modals). */
  sortField: string | null = null;
  sortDir: 'asc' | 'desc' = 'asc';

  listLoading = true;
  loadError: string | null = null;

  @Input('row-data')
  set rowDataInput(v: string | null | Record<string, unknown>) {
    try {
      if (v == null || v === '') {
        this.pendingRowData = null;
        return;
      }
      const parsed =
        typeof v === 'string'
          ? safeJsonInput<any>(v, null, 'view-bulk-export-selection.parse1')
          : v;
      this.pendingRowData = parsed && typeof parsed === 'object' ? parsed : null;
      this.tryInit();
    } catch {
      this.pendingRowData = null;
    }
  }

  @Input('header-data')
  set headerDataInput(v: string | null | Record<string, unknown>) {
    try {
      if (v == null || v === '') {
        this.pendingHeader = null;
        return;
      }
      const parsed =
        typeof v === 'string'
          ? safeJsonInput<any>(v, null, 'view-bulk-export-selection.parse2')
          : v;
      this.pendingHeader = parsed && typeof parsed === 'object' ? parsed : null;
      this.tryInit();
    } catch {
      this.pendingHeader = null;
    }
  }

  @Input('modal-name')
  set modalNameInput(v: string | null) {
    this.pendingModalName = (v || '').trim();
    this.tryInit();
  }

  /** CeHostDialog passes grid column metadata; some configs expose ids here. */
  @Input('col-data')
  set colDataInput(v: string | null | Record<string, unknown>) {
    try {
      if (v == null || v === '') {
        this.pendingColData = null;
        return;
      }
      const parsed = typeof v === 'string' ? safeJsonInput<any>(v, null, 'view-bulk-export-selection.parse3') : v;
      this.pendingColData = parsed && typeof parsed === 'object' ? parsed : null;
      this.tryInit();
    } catch {
      this.pendingColData = null;
    }
  }

  @Input('grid-data')
  set gridDataInput(v: string | null | Record<string, unknown>) {
    try {
      if (v == null || v === '') {
        this.pendingGridData = null;
        return;
      }
      const parsed = typeof v === 'string' ? safeJsonInput<any>(v, null, 'view-bulk-export-selection.parse4') : v;
      this.pendingGridData = parsed && typeof parsed === 'object' ? parsed : null;
      this.tryInit();
    } catch {
      this.pendingGridData = null;
    }
  }

  @Input('base-urls')
  set baseUrlsInput(v: string | null | Record<string, unknown>) {
    try {
      if (v == null || v === '') {
        this.pendingBaseUrls = null;
        return;
      }
      const parsed = typeof v === 'string' ? safeJsonInput<any>(v, null, 'view-bulk-export-selection.parse5') : v;
      this.pendingBaseUrls = parsed && typeof parsed === 'object' ? parsed : null;
      this.tryInit();
    } catch {
      this.pendingBaseUrls = null;
    }
  }

  constructor(
    private readonly formViewService: FormViewService,
    private readonly snackbar: GtwSnackbarService,
    private readonly cdr: ChangeDetectorRef,
    private readonly elRef: ElementRef<HTMLElement>,
    private readonly workspace: WorkspaceService,
    private readonly appService: AppService,
    private readonly globalService: GlobalService,
  ) {}

  ngAfterViewInit(): void {
    // CE: attributes are on the host tag; re-run init after view in case @Input
    // setters did not run before the first scheduled runInit (timing/order).
    this.hydratePendingFromHostElement();
    this.tryInit();
  }

  ngOnDestroy(): void {
    if (this.initTimer != null) {
      clearTimeout(this.initTimer);
      this.initTimer = null;
    }
    this.subs.unsubscribe();
  }

  /**
   * Coalesce CE attribute setters (`row-data`, `header-data`, `modal-name`)
   * so we load once with the full contract from CeHostDialog.
   */
  private tryInit(): void {
    if (this.initTimer != null) {
      clearTimeout(this.initTimer);
    }
    this.initTimer = setTimeout(() => this.runInit(), 0);
  }

  /**
   * Custom-element host may not equal `ElementRef` (inner component root); walk up
   * a few levels to find the node that carries CeHostDialog JSON attributes.
   */
  private getCeHostElement(): HTMLElement | null {
    let el: HTMLElement | null = this.elRef?.nativeElement;
    for (let i = 0; i < 5 && el; i++) {
      if (el.getAttribute?.('header-data') || el.getAttribute?.('row-data')) {
        return el;
      }
      el = el.parentElement;
    }
    return this.elRef?.nativeElement ?? null;
  }

  /** Read JSON attributes from the custom element host (parity with CeHostDialog setAttribute). */
  private hydratePendingFromHostElement(): void {
    const el = this.getCeHostElement();
    if (!el?.getAttribute) {
      return;
    }
    const parse = (name: string): any => {
      const s = el.getAttribute(name);
      if (s == null || s === '') {
        return null;
      }
      try {
        const o = safeJsonInput<any>(s, null, 'view-bulk-export-selection.parse6');
        return o && typeof o === 'object' ? o : null;
      } catch {
        return null;
      }
    };
    if (!this.pendingHeader) {
      this.pendingHeader = parse('header-data');
    }
    if (!this.pendingRowData || Object.keys(this.pendingRowData).length === 0) {
      const r = parse('row-data');
      if (r) {
        this.pendingRowData = r;
      }
    }
    if (!this.pendingGridData) {
      this.pendingGridData = parse('grid-data');
    }
    if (!this.pendingColData) {
      this.pendingColData = parse('col-data');
    }
    if (!this.pendingBaseUrls || Object.keys(this.pendingBaseUrls).length === 0) {
      this.pendingBaseUrls = parse('base-urls');
    }
    if (!this.pendingModalName) {
      this.pendingModalName = (el.getAttribute('modal-name') || '').trim();
    }
  }

  /** Prefer explicit row-data, then grid callback rowData, then CeHostDialog-bridged activeScreen row. */
  private rowPayloadForResolution(): any {
    const nonEmptyObject = (o: any): any => {
      if (o == null || typeof o !== 'object' || Array.isArray(o)) {
        return null;
      }
      return Object.keys(o).length > 0 ? o : null;
    };

    const fromWorkspace = (): any => {
      const active = this.workspace?.workspaceFactory?.activeScreen;
      const row = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(active));
      return nonEmptyObject(row);
    };

    return (
      nonEmptyObject(this.pendingRowData) ||
      nonEmptyObject(this.pendingGridData?.rowData) ||
      fromWorkspace() ||
      {}
    );
  }

  /**
   * Grid / CeHostDialog may pass `{ data: { … } }` while `REQUEST_ID` stays on
   * the envelope — legacy modal used `rowData.REQUEST_ID` from the resolved row.
   */
  private effectiveGridRow(raw: any): any {
    if (raw == null || typeof raw !== 'object') {
      return null;
    }
    let envelope: any = raw;
    if (Array.isArray(raw)) {
      if (raw.length === 0) {
        return null;
      }
      envelope = raw[0];
    }
    const inner =
      envelope?.data != null && typeof envelope.data === 'object'
        ? envelope.data
        : envelope;
    const merged = { ...inner };
    const idKeys = ['REQUEST_ID', 'request_id', 'P_REQUEST_ID', 'p_request_id'];
    if (envelope && typeof envelope === 'object') {
      for (const k of idKeys) {
        const v = envelope[k];
        if (v != null && v !== '' && (merged[k] == null || merged[k] === '')) {
          merged[k] = v;
        }
      }
    }
    return merged;
  }

  private resolveRequestId(): string | null {
    const primary = this.rowPayloadForResolution();
    const rowNorm = this.effectiveGridRow(primary);
    const raw = primary;
    const rawEnvelope =
      Array.isArray(raw) && raw.length > 0 && raw[0] != null && typeof raw[0] === 'object'
        ? raw[0]
        : raw;
    const col = this.pendingColData;
    const candidates = [
      rowNorm?.REQUEST_ID,
      rowNorm?.request_id,
      rowNorm?.P_REQUEST_ID,
      rowNorm?.p_request_id,
      rawEnvelope?.REQUEST_ID,
      rawEnvelope?.request_id,
      rawEnvelope?.P_REQUEST_ID,
      rawEnvelope?.p_request_id,
      col?.REQUEST_ID,
      col?.request_id,
      col?.p_request_id,
      col?.P_REQUEST_ID,
    ];
    for (const c of candidates) {
      if (c != null && String(c).trim() !== '') {
        return String(c).trim();
      }
    }
    return null;
  }

  private normalizeJsonRows(raw: any): any[] {
    let v = raw;
    if (typeof v === 'string') {
      try {
        v = safeJsonInput<any>(v, null, 'view-bulk-export-selection.parse7');
      } catch {
        return [];
      }
    }
    if (v == null) {
      return [];
    }
    if (Array.isArray(v)) {
      return v;
    }
    if (typeof v === 'object') {
      return [v];
    }
    return [];
  }

  private runInit(): void {
    this.initTimer = null;
    if (this.loadStarted) {
      return;
    }
    this.hydratePendingFromHostElement();
    if (!this.pendingHeader) {
      return;
    }
    const primary = this.rowPayloadForResolution();
    const rowNorm = this.effectiveGridRow(primary);
    const requestId = this.resolveRequestId();
    if (requestId == null || requestId === '') {
      this.loadError = 'Could not read request id from the selected row.';
      this.listLoading = false;
      this.cdr.markForCheck();
      return;
    }

    this.loadStarted = true;
    this.rowData = rowNorm ?? primary;
    this.headerData = this.pendingHeader;
    this.modalName = this.pendingModalName;

    // `component-registry.service.ts`: `viewEntitiesSelections` vs `viewFormsSelections`
    const mn = this.modalName.toLowerCase();
    if (mn.includes('viewentitiesselections') || mn.includes('entity')) {
      this.selectionMode = 'ENTITY';
      this.requestedType = 'Entities';
      // formViewCtrl.js ViewBulkExportEntitySelectionCtrl — columns L1494–1508
      this.columns = [
        {
          field: 'ENTITY',
          header: 'Entity',
          widthPx: 120,
          altFields: ['ENTITY_NAME', 'LE_NAME', 'entity'],
        },
        {
          field: 'CDR_NO',
          header: 'Cdr No',
          widthPx: 100,
          altFields: ['CDR_NUMBER', 'MARS', 'cdr_no'],
        },
        {
          field: 'REPORTING_PERIOD',
          header: 'Reporting Period',
          widthPx: 100,
          altFields: ['REPORTING_PERIOD_DESC', 'reporting_period'],
        },
      ];
    } else {
      this.selectionMode = 'FORM';
      this.requestedType = 'Forms';
      // formViewCtrl.js ViewBulkExportSelectionCtrl — columns L1552–1568
      this.columns = [
        {
          field: 'IRS_FORM_NO',
          header: 'Irs Form No',
          altFields: ['IRS_FORM_NUMBER', 'irs_form_no', 'FORM_NO', 'IRS_FORM'],
        },
        {
          field: 'WPIRS_FORM_NO',
          header: 'Parent Form Name',
          altFields: [
            'WPIRS_FORM_NUMBER',
            'wpirs_form_no',
            'PARENT_IRS_FORM_NO',
            'PARENT_FORM_NAME',
          ],
        },
        {
          field: 'SCHEDULE',
          header: 'Schedule',
          altFields: ['FORM_SCHEDULE', 'SCHEDULE_NAME', 'schedule'],
        },
        {
          field: 'LINE',
          header: 'Line',
          altFields: ['FORM_LINE', 'LINE_NUMBER', 'LINE_NO', 'line'],
        },
      ];
    }

    this.sortField = this.columns[0]?.field ?? null;
    this.sortDir = 'asc';

    const us = this.headerData.userSettingsData || {};
    this.currentTheme = us.user?.selected_theme || us.selected_theme || 'theme-black';

    this.loadDetails(String(requestId));
  }

  /**
   * Legacy `GENERAL_CONFIG.base_url` + Angular `prepareDialogData` spread `environment`
   * onto `generalConfigData`; also allow `base-urls` from the dialog and the build
   * `environment` object when the stringified header is incomplete (CE / hydration).
   */
  private resolveLoadJsonBaseUrl(): string {
    const gc = this.headerData?.generalConfigData || {};
    const fromHeader =
      gc.base_url ||
      gc.api ||
      (gc as any).BASE_URL ||
      (gc as any).API_URL ||
      '';
    const fromBaseUrls =
      this.pendingBaseUrls?.api ||
      this.pendingBaseUrls?.base_url ||
      '';
    const fallback =
      (environment as any).base_url ||
      (environment as any).api ||
      '/gtw';
    const raw = String(fromHeader || fromBaseUrls || fallback).trim();
    return raw.replace(/\/+$/, '');
  }

  /**
   * Legacy `USER_SETTINGS.user.sso_id` — CeHostDialog reads `sessionStorage.user_settings`,
   * but the shell often only has `AppService.USER_SETTINGS` populated.
   */
  private resolveSsoId(): string {
    const gp = this.headerData?.globalParams || {};
    let id =
      this.headerData?.userSettingsData?.user?.sso_id ||
      this.headerData?.userSettingsData?.sso_id ||
      gp.sso_id ||
      gp.SSO_ID ||
      (gp as any).georaclehrid ||
      '';
    id = id != null && id !== '' ? String(id).trim() : '';
    if (!id) {
      try {
        const u = safeJsonInput<any>(sessionStorage.getItem('user_settings') || '{}', null, 'view-bulk-export-selection.parse8');
        id = String(u?.user?.sso_id || u?.sso_id || '').trim();
      } catch {
        /* ignore */
      }
    }
    if (!id && this.appService?.USER_SETTINGS) {
      const us = this.appService.USER_SETTINGS;
      id = String(us?.user?.sso_id || us?.sso_id || '').trim();
    }
    if (!id) {
      const g = this.globalService?.global?.globalParams || {};
      id = String(g.sso_id || g.SSO_ID || '').trim();
    }
    return id;
  }

  private loadDetails(requestId: string): void {
    const baseUrl = this.resolveLoadJsonBaseUrl();
    const ssoId = this.resolveSsoId();

    if (!baseUrl || !ssoId) {
      this.loadStarted = false;
      this.loadError = 'Missing configuration (base URL or user).';
      this.listLoading = false;
      this.cdr.markForCheck();
      return;
    }

    const getEntsFrms = this.selectionMode === 'ENTITY' ? 'ENTITY' : 'FORM';

    this.listLoading = true;
    this.loadError = null;
    this.cdr.markForCheck();

    this.subs.add(
      this.formViewService.getRequestDetails(baseUrl, ssoId, requestId, getEntsFrms).subscribe({
        next: (res: any) => {
          const body = res?.data != null && typeof res.data === 'object' ? res.data : res;
          if (body?.callSuccess != null && String(body.callSuccess) !== '1') {
            const msg =
              body.errorMessage && String(body.errorMessage) !== 'null'
                ? String(body.errorMessage)
                : 'Request failed.';
            this.loadError = msg;
            this.rows = [];
            this.filteredRows = [];
            this.sortField = this.columns[0]?.field ?? null;
            this.listLoading = false;
            this.snackbar.openSnackBar(msg, 'gtw-snackbar--red');
            this.cdr.markForCheck();
            return;
          }
          const raw = body?.jsonObject ?? res?.jsonObject ?? res?.data?.jsonObject;
          this.rows = this.normalizeJsonRows(raw);
          if (!this.sortField && this.columns.length) {
            this.sortField = this.columns[0].field;
            this.sortDir = 'asc';
          }
          this.refreshDisplayRows();
          this.listLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.listLoading = false;
          this.loadError = 'Failed to load request details.';
          this.snackbar.openSnackBar(this.loadError, 'gtw-snackbar--red');
          this.cdr.markForCheck();
        },
      }),
    );
  }

  onSearchChange(value: string): void {
    this.searchTerm = value ?? '';
    this.refreshDisplayRows();
    this.cdr.markForCheck();
  }

  onColumnSort(col: ViewBulkExportColumn): void {
    if (this.sortField === col.field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = col.field;
      this.sortDir = 'asc';
    }
    this.refreshDisplayRows();
    this.cdr.markForCheck();
  }

  sortIconClasses(col: ViewBulkExportColumn): string {
    if (this.sortField !== col.field) {
      return 'fa fa-sort sort-icon--inactive';
    }
    return this.sortDir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc';
  }

  /**
   * Emphasized row when API sends a flag (optional; zebra striping covers DataTables-style rows).
   */
  isRowHighlighted(row: any): boolean {
    if (!row || typeof row !== 'object') {
      return false;
    }
    const y = (v: unknown) => String(v ?? '').toUpperCase() === 'Y' || v === true || v === 1;
    return (
      y(row.HIGHLIGHT) ||
      y(row.HIGHLIGHT_ROW) ||
      y(row.EMPHASIS) ||
      y(row.SELECTED_ROW) ||
      String(row.ROW_STYLE || '')
        .toLowerCase()
        .includes('highlight')
    );
  }

  private refreshDisplayRows(): void {
    let list: any[];
    const q = (this.searchTerm || '').trim().toLowerCase();
    if (!q) {
      list = [...this.rows];
    } else {
      list = this.rows.filter((row) => {
        return this.columns.some((col) => {
          const cell = this.cellRawValue(row, col);
          return String(cell ?? '')
            .toLowerCase()
            .includes(q);
        });
      });
    }

    const field = this.sortField;
    const col = field ? this.columns.find((c) => c.field === field) : null;
    if (col && list.length > 1) {
      const dir = this.sortDir === 'asc' ? 1 : -1;
      list = [...list].sort((a, b) => this.compareSortValues(this.cellRawValue(a, col), this.cellRawValue(b, col)) * dir);
    }

    this.filteredRows = list;
  }

  private compareSortValues(a: unknown, b: unknown): number {
    const sa = String(a ?? '').trim();
    const sb = String(b ?? '').trim();
    return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
  }

  /** First non-empty value for `field` and optional `altFields` (case-sensitive then UPPER). */
  private cellRawValue(row: any, col: ViewBulkExportColumn): unknown {
    const keys = [col.field, ...(col.altFields || [])];
    for (const k of keys) {
      if (!k) {
        continue;
      }
      let v = row?.[k];
      if (v != null && v !== '') {
        return v;
      }
      const upper = String(k).toUpperCase();
      if (upper !== k) {
        v = row?.[upper];
        if (v != null && v !== '') {
          return v;
        }
      }
    }
    return undefined;
  }

  cellValue(row: any, col: ViewBulkExportColumn): string {
    const v = this.cellRawValue(row, col);
    return v != null && v !== '' ? String(v) : '';
  }

  emptyMessage(): string {
    return this.selectionMode === 'ENTITY'
      ? 'No Selected Entities were found.'
      : 'No Selected Forms were found.';
  }

  close(): void {
    this.requestGridUpdate.emit('cancel');
  }

  trackByIndex(index: number): number {
    return index;
  }
}
